import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LoadingFileUpload from "@/components/ui/loadingFileUpload";
import { toast } from "@/components/ui/use-toast";
import useImageCompressor from "@/hooks/useImageCompressor";
import { useMutateData } from "@/hooks/useMutation";
import usePostMultipart from "@/hooks/usePostMultipart";
import { Edit } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const AddFunder = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "financeur",
  });
  const { handleCompressImage } = useImageCompressor();
  const { postMultipart } = usePostMultipart();
  const [picture, setPicture] = useState({
    uri: "",
    alt: "",
  });
  const [uploadLogoLoading, setUploadLogoLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    representative: "",
    nifStat: "",
    address: "",
    entity: "",
    amountPaid: "",
  });

  // Mutation hook
  const { mutate, isLoading: isSubmitting } = useMutateData({
    id: "funders",
    method: "POST",
    onSuccess: () => {
      toast({ title: t("Financeur ajouté avec succès") });
    },
    onError: (error) => {
      toast({
        title: t("Une erreur est survenue"),
        description: error.message,
      });
    },
  });

  const handlePictureChange = async (value) => {
    if (value) {
      setUploadLogoLoading(true);
      const data = new FormData();

      data.append("type", 5);
      const fileDefaultName = value[0].name;

      // Compresser l'image avant l'upload
      const compressedBlob = await handleCompressImage(value[0], 500, 300, 0.9);
      if (compressedBlob) {
        for (let i = 0; i < value.length; i++) {
          const renamedFile = new File(
            [compressedBlob],            // contenu
            fileDefaultName,           // nouveau nom
            { type: compressedBlob.type } // garder le type MIME d'origine
          );
          data.append("files[]", renamedFile);
        }
      } else {
        for (let i = 0; i < value.length; i++) {
          data.append("files[]", value[i]);
        }
      }

      const newMedias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data,
      });
      if (
        !Array.isArray(newMedias) &&
        newMedias.success !== undefined &&
        newMedias.success !== null &&
        !newMedias.success
      ) {
        toast({
          title: newMedias.message,
          duration: 1000,
        });
        setUploadLogoLoading(false);
        return;
      }

      setPicture(newMedias[0]);
      setUploadLogoLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      picture: picture.uri,
    };

    // mutate({
    //   endpoint: `${import.meta.env.VITE_API_ENDPOINT}/funders`,
    //   body: payload,
    // });
  };

  return (
    <div>
      <ReactBreadcrumb
        items={[
          {
            href: "/dashboard/programs",
            name: "Mes programmes",
          },
          {
            href: "/dashboard/mes-financeurs",
            name: "Mes financeurs",
          },
          {
            href: ``,
            name: "Ajout financeur",
          },
        ]}
        isTruncated={false}
      />

      <h1 className="mb-2 mt-4 text-[24px] font-medium inline-block">
        {t("Ajout financeur")}
      </h1>

      <form onSubmit={handleSubmit} className="w-1/3 mt-7">
        <label htmlFor="photo">{t("Ajouter photo")}</label>

        <div className="size-24 my-4 relative">
          <FileUploader
            value={[]}
            onValueChange={handlePictureChange}
            dropzoneOptions={{
              multiple: false,
            }}
            className={`size-full absolute right-0 top-0`}
          >
            <FileInput className="size-full rounded-[20px] border border-dashed border-background/50">
              <div className="absolute right-4 top-4 flex size-8 flex-col items-center justify-center bg-background ">
                <Edit />
              </div>

              {uploadLogoLoading && <LoadingFileUpload />}
            </FileInput>
          </FileUploader>
          {!uploadLogoLoading && picture.uri !== "" && (
            <img
              src={import.meta.env.VITE_MEDIA_BASE + picture.uri}
              alt={picture.alt}
              className="size-full object-cover transition-all hover:blur-0 group-hover:blur-0"
            />
          )}
          {!uploadLogoLoading && picture.uri === "" && (
            <img
              src="/placeholder-photo.png"
              alt="placeholder"
              className="size-full object-cover transition-all hover:blur-0 group-hover:blur-0"
            />
          )}
        </div>
        <div className="mb-3">
          <Input
            name="firstName"
            placeholder={t("Nom du financeur")}
            value={formData.firstName}
            onChange={handleInputChange}
          />
        </div>
        <div className="mb-3">
          <Input
            name="lastName"
            placeholder={t("Prénom du financeur")}
            value={formData.lastName}
            onChange={handleInputChange}
          />
        </div>
        <div className="mb-3">
          <Input
            name="representative"
            placeholder={t("Représentant")}
            value={formData.representative}
            onChange={handleInputChange}
          />
        </div>
        <div className="mb-3">
          <Input
            name="nifStat"
            placeholder="NIF/STAT"
            value={formData.nifStat}
            onChange={handleInputChange}
          />
        </div>
        <div className="mb-3">
          <Input
            name="address"
            placeholder={t("Adresse")}
            value={formData.address}
            onChange={handleInputChange}
          />
        </div>
        <div className="mb-3">
          <Input
            name="entity"
            placeholder={t("Entité")}
            value={formData.entity}
            onChange={handleInputChange}
          />
        </div>
        <div className="mb-3">
          <Input
            name="amountPaid"
            placeholder={t("Montant versé")}
            value={formData.amountPaid}
            onChange={handleInputChange}
          />
        </div>
        <Button
          type="submit"
          variant={"roundedBgOrange"}
          disabled={isSubmitting}
        >
          {isSubmitting ? t("Ajout en cours...") : t("Ajouter")}
        </Button>
      </form>
    </div>
  );
};

export default AddFunder;
